import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { SearchProcedureResultWrapper, SearchProceduresResult } from 'ts-types/api.types';
import { Button, Grid, Input, Modal } from 'semantic-ui-react';
import { SearchBy } from 'ts-types/api.enums';
import styled from 'styled-components';
import SearchProceduresTableSegment from 'components/segments/procedures/SearchProceduresTableSegment';
import { IndexedSearchData } from 'components/segments/procedures/ProceduresSegment';
import { debounce } from 'ts-debounce';
import { searchForProcedures } from 'services/searchProceduresServices';
import axios, { CancelTokenSource } from 'axios';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import SearchByButtonsStyle from 'components/layout/SearchByButtonsStyle';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PxGrid from 'components/layout/PxGrid';

const StyledInput = styled(Input)`

   &.ui.input > input {
      padding: 5px 5px;
   }

   &.ui.mini.input > input {
     font-size: 1rem;
   }
`;

interface Props extends MessageSourceProps {
  modalOpen: boolean,
  onCloseModal: () => void,
  onSelectProcedure: (entry: Partial<SearchProceduresResult>) => void,
  setSearchProceduresSearchId: (searchId?: string) => void,
  searchData: Partial<IndexedSearchData>
}

interface State {
  foundProcedures: Array<SearchProceduresResult>,
  searchText?: string,
  searchBy: SearchBy,
  proceduresLoading: boolean,
  selectedProcedureId?: number,
  searchId?: string,
  cancelTokenSource: CancelTokenSource
}

class SearchProceduresModal extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundProcedures: [],
      searchBy: SearchBy.CODE,
      proceduresLoading: false,
      selectedProcedureId: -1,
      cancelTokenSource
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  onSearchTextChanged = (e: any) => {

    const {searchBy} = this.state;
    const {value} = e.target;

    this.setState({
      searchText: value
    });

    if (!!value && value != "") {
      this.searchProcedures(value, searchBy);
    } else {
      this.setState({
        foundProcedures: []
      })
    }
  };

  changeSearchByTo = (searchBy: SearchBy) => {

    const {searchText} = this.state;

    this.setState({
      searchBy: searchBy
    });

    if (!!searchText && searchText != "") {
      this.searchProcedures(searchText, searchBy);
    }
  };

  searchProcedures = debounce((searchText?: string, searchBy?: SearchBy) => {

    if (!this.mounted) {
      return;
    }

    const {searchData, setSearchProceduresSearchId} = this.props;
    const {searchId, cancelTokenSource} = this.state;

    this.setState({
      proceduresLoading: true
    });

    const modifiedSearchData = {
      ...searchData,
      search: searchText,
      searchBy: searchBy,
      searchId: searchId
    };

    const onFinally = () => {
      this.setState({
        proceduresLoading: false
      })
    };

    searchForProcedures(modifiedSearchData, cancelTokenSource)
        .then((response: SearchProcedureResultWrapper) => {
          this.setState({
            foundProcedures: response.searchProcedureResults,
            searchId: response.searchId
          });

          setSearchProceduresSearchId(response.searchId);
        })
        .catch(() => this.setState({foundProcedures: []}))
        .finally(onFinally);
  }, 400);

  markProcedure = (procedureId?: number) => {

    const {selectedProcedureId} = this.state;

    if (selectedProcedureId != procedureId) {
      this.setState({
        selectedProcedureId: procedureId
      })
    }
  };

  onSelectProcedure = () => {

    const {selectedProcedureId, foundProcedures} = this.state;
    const {onSelectProcedure} = this.props;

    if (!!selectedProcedureId) {
      const foundProcedure = foundProcedures.find(procedure => procedure.id == selectedProcedureId);
      if (foundProcedure) {
        onSelectProcedure(foundProcedure);
      }
    }

    this.onCloseModal();

    this.setState({
      foundProcedures: [],
      searchBy: SearchBy.CODE,
      searchText: undefined,
      selectedProcedureId: undefined
    })
  };

  onCloseModal = () => {

    const {onCloseModal} = this.props;

    onCloseModal();

    this.setState({
      foundProcedures: [],
      searchBy: SearchBy.CODE,
      searchText: undefined,
      selectedProcedureId: undefined
    })
  };

  onSelectProcedureFromTable = (entry: SearchProceduresResult) => {

    const {onSelectProcedure} = this.props;

    onSelectProcedure(entry);
    this.onCloseModal();
  };

  render(): React.ReactNode {

    const {modalOpen, getMessage} = this.props;
    const {searchText, searchBy, proceduresLoading, foundProcedures, selectedProcedureId} = this.state;

    return (
        <Modal
            open={modalOpen}
            onClose={this.onCloseModal}
            style={{width: "650px", height: "485px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {getMessage("procedures")}
          </Modal.Header>
          <FormModalContentContainer>
            <PxGrid>
              <Grid.Row>
                <Grid.Column verticalAlign="middle" width={2}>{getMessage("search")}:</Grid.Column>
                <Grid.Column verticalAlign="middle" width={8}>
                  <StyledInput defaultValue={searchText} onChange={this.onSearchTextChanged} fluid autoFocus/>
                </Grid.Column>
                <Grid.Column verticalAlign="middle" width={6}>
                  <SearchByButtonsStyle compact size={"tiny"}>
                    <Button
                        compact
                        primary={searchBy == SearchBy.CODE}
                        basic={searchBy != SearchBy.CODE}
                        onClick={() => this.changeSearchByTo(SearchBy.CODE)}
                        type={"button"}
                    >
                      {getMessage("code")}
                    </Button>
                    <Button
                        compact
                        primary={searchBy == SearchBy.DESCRIPTION}
                        basic={searchBy != SearchBy.DESCRIPTION}
                        onClick={() => this.changeSearchByTo(SearchBy.DESCRIPTION)}
                        type={"button"}
                    >
                      {getMessage("description")}
                    </Button>
                  </SearchByButtonsStyle>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <SearchProceduresTableSegment
                      proceduresLoading={proceduresLoading}
                      selectedProcedure={selectedProcedureId}
                      onSelectProcedure={this.onSelectProcedureFromTable}
                      foundProcedures={foundProcedures}
                      markProcedure={this.markProcedure}
                  />
                </Grid.Column>
              </Grid.Row>
            </PxGrid>

            <FormButtonsRow>
              <Button compact primary
                      onClick={this.onSelectProcedure}>{getMessage("button.save")}</Button>
              <Button compact
                      onClick={this.onCloseModal}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormModalContentContainer>

        </Modal>
    );
  }
}

export default withMessages(SearchProceduresModal);
